import { useEffect, useId, useRef, useState } from "react"
import { apiFetch } from "../../lib/api"

export type MunicipioOption = {
  ibge: string
  nome: string
  uf: string
}

type MunicipioIbgeSelectProps = {
  uf: string
  ibge: string
  municipioNome: string
  onSelect: (ibge: string, nome: string) => void
  disabled?: boolean
  searchPath?: string
}

export const MunicipioIbgeSelect = ({
  uf,
  ibge,
  municipioNome,
  onSelect,
  disabled = false,
  searchPath = "/v1/admin/municipios",
}: MunicipioIbgeSelectProps) => {
  const listId = useId()
  const wrapRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState("")
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<MunicipioOption[]>([])
  const [searchError, setSearchError] = useState<string | null>(null)

  const displayValue =
    ibge && municipioNome ? `${municipioNome} — IBGE ${ibge}` : ibge ? `IBGE ${ibge}` : query

  useEffect(() => {
    if (!open) {
      setQuery(ibge && municipioNome ? municipioNome : "")
    }
  }, [ibge, municipioNome, open])

  useEffect(() => {
    if (!open || disabled) return
    const term = query.trim()
    if (term.length < 2) {
      setItems([])
      setSearchError(term.length === 1 ? "Digite ao menos 2 letras para buscar." : null)
      return
    }
    if (uf.length !== 2) {
      setItems([])
      setSearchError("Selecione a UF antes de buscar o município.")
      return
    }

    const timer = window.setTimeout(async () => {
      setLoading(true)
      setSearchError(null)
      try {
        const res = await apiFetch<{ items: MunicipioOption[] }>(
          `${searchPath}?uf=${encodeURIComponent(uf)}&q=${encodeURIComponent(term)}&limit=25`,
        )
        setItems(res.items ?? [])
        if (!res.items?.length) {
          setSearchError("Nenhum município encontrado. Tente outro nome.")
        }
      } catch (err) {
        setItems([])
        setSearchError(err instanceof Error ? err.message : "Falha ao buscar municípios.")
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => window.clearTimeout(timer)
  }, [open, query, uf, disabled, searchPath])

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handlePointerDown)
    return () => document.removeEventListener("mousedown", handlePointerDown)
  }, [])

  const handleSelect = (item: MunicipioOption) => {
    onSelect(item.ibge, item.nome)
    setQuery(item.nome)
    setOpen(false)
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label="Município (código IBGE)"
        disabled={disabled}
        value={open ? query : displayValue}
        placeholder="Busque pelo nome da cidade (mín. 2 letras)"
        className="w-full rounded-lg border px-3 py-2 text-sm"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          if (ibge) {
            onSelect("", "")
          }
        }}
      />
      {open ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {loading ? (
            <li className="px-3 py-2 text-sm text-slate-500">Buscando…</li>
          ) : null}
          {!loading && searchError ? (
            <li className="px-3 py-2 text-sm text-slate-500">{searchError}</li>
          ) : null}
          {!loading &&
            items.map((item) => (
              <li key={item.ibge}>
                <button
                  type="button"
                  role="option"
                  aria-selected={item.ibge === ibge}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(item)}
                >
                  <span className="font-medium text-slate-900">{item.nome}</span>
                  <span className="ml-2 text-xs text-slate-500">IBGE {item.ibge}</span>
                </button>
              </li>
            ))}
        </ul>
      ) : null}
      {ibge ? (
        <input type="hidden" name="codigo_municipio" value={ibge} required />
      ) : (
        <p className="mt-1 text-xs text-amber-700">Selecione um município na lista (Connect-API / IBGE).</p>
      )}
    </div>
  )
}
